<template>
  <div>
    <table-pagination class="mb-1" :pagination="pagination" :counter-label="$t('backup.tabs.clients')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <b-table responsive class="bk-table" :items="rows" :fields="fields"
             no-local-sorting :sort-by="sort.field" :sort-desc="sortIsDesc"
             :busy="loading" hover show-empty
             :tbody-tr-class="rowClass"
             @sort-changed="onSortChanged" @row-clicked="row => $emit('open-client', row.backupId)">
      <template #table-busy>
        <div class="text-center text-primary my-2"><b-spinner small class="mr-50"/>{{ $t('backup.loading') }}</div>
      </template>
      <template #empty>
        <backup-table-state :error="loadError ? errorText() : null" icon="fa-solid fa-user-slash"
                            :title="$t('backup.emptyClients')" :hint="$t('backup.emptyHint')" @retry="refresh"/>
      </template>

      <template #cell(client)="{ item }">
        <div class="bk-client">
          <span class="bk-avatar" :class="`bk-avatar--${(item.matchStatus || '').toLowerCase()}`">{{ bkInitials(item) }}</span>
          <div class="bk-client__text">
            <div class="bk-client__name">{{ bkClientName(item) }} <span class="bk-id">#{{ item.backupId }}</span></div>
            <div class="bk-client__sub">
              <span v-if="item.email">{{ item.email }}</span>
              <span v-if="item.phone">· {{ item.phone }}</span>
              <span v-if="item.country">· {{ item.country }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #cell(orgId)="{ item }">
        <div class="bk-two">
          <span>{{ getOrganizationNameById(item.orgId) || '—' }}</span>
          <small v-if="item.businessUnitId">{{ getBusinessUnitNameById(item.businessUnitId) }}</small>
        </div>
      </template>
      <template #cell(statusId)="{ item }">
        <div class="bk-two">
          <span v-if="item.statusId" class="bk-cstatus" :style="{ color: bkClientStatusColor(item.statusId) }">{{ bkClientStatusName(item.statusId) }}</span>
          <span v-else class="text-muted">—</span>
          <small v-if="item.representativeUserId"><i class="fa-solid fa-user-tie mr-25"></i>{{ bkUserName(item.representativeUserId) }}</small>
        </div>
      </template>
      <template #cell(creationTime)="{ value }">
        <div class="bk-two"><span>{{ bkDay(value) }}</span><small>{{ bkTime(value) }}</small></div>
      </template>
      <template #cell(matchStatus)="{ item }">
        <backup-status-badge :status="item.matchStatus" :reason="item.matchReason"
                             :current-id="item.currentId" :client-id="item.currentId"/>
      </template>
      <template #cell(missing)="{ item }">
        <div class="bk-missing">
          <span v-for="m in missingParts(item)" :key="m.key" class="bk-missing__item"
                :class="{ 'is-zero': !m.n }" v-b-tooltip.hover.top="$t(`backup.missingTip.${m.key}`, { n: m.n })">
            <i :class="m.icon"></i>{{ m.n }}
          </span>
          <span v-if="item.fieldDiffs" class="bk-missing__item is-diff"
                v-b-tooltip.hover.top="$t('backup.missingTip.fields', { n: item.fieldDiffs })">
            <i class="fa-solid fa-code-compare"></i>{{ item.fieldDiffs }}
          </span>
        </div>
      </template>
      <template #cell(actions)="{ item }">
        <b-button size="sm" :variant="hasWork(item) ? 'primary' : 'outline-primary'" class="bk-open-btn no-click"
                  @click.stop="$emit('open-client', item.backupId)">
          <i class="fa-solid fa-arrow-right-to-bracket mr-25"></i>{{ hasWork(item) ? $t('backup.review') : $t('backup.view') }}
        </b-button>
      </template>
    </b-table>

    <table-pagination v-if="rows.length > 10" class="mt-1" :pagination="pagination"
                      :counter-label="$t('backup.tabs.clients')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBackupTable from '@/components/mixin/mixinBackupTable'
import { isRestorable } from '@/components/mixin/mixinBackup'
import TablePagination from '@/components/widget/TablePagination.vue'
import BackupStatusBadge from '@/components/partial/backup/BackupStatusBadge.vue'
import BackupTableState from '@/components/partial/backup/BackupTableState.vue'

export default {
  name: 'BackupClientsTable',
  components: { TablePagination, BackupStatusBadge, BackupTableState },
  mixins: [mixinBackupTable],
  data: () => ({
    defaultSortField: 'creationTime',
  }),
  computed: {
    fields() {
      return [
        { key: 'client', label: this.$t('backup.columns.client'), sortable: true, class: 'bk-col-left' },
        { key: 'orgId', label: this.$t('backup.columns.organization') },
        { key: 'statusId', label: this.$t('backup.columns.statusAndRep') },
        { key: 'creationTime', label: this.$t('backup.columns.creationTime'), sortable: true },
        { key: 'matchStatus', label: this.$t('backup.columns.match'), sortable: true },
        { key: 'missing', label: this.$t('backup.columns.missingItems') },
        { key: 'actions', label: '', class: 'bk-col-right' },
      ]
    },
  },
  methods: {
    ...mapActions('backup', ['getAllBackupClients']),
    fetchRows(params) {
      return this.getAllBackupClients(params)
    },
    onSortChanged(ctx) {
      const field = ctx.sortBy === 'client' ? 'firstName' : ctx.sortBy
      this.setSort(field, ctx.sortDesc ? 'DESC' : 'ASC')
      this.pagination.page = 1
      this.refresh()
    },
    missingParts(item) {
      const m = item.missing || {}
      return [
        { key: 'accounts', icon: 'fa-solid fa-id-card', n: Number(m.accounts) || 0 },
        { key: 'monetary', icon: 'fa-solid fa-coins', n: Number(m.monetary) || 0 },
        { key: 'trades', icon: 'fa-solid fa-chart-line', n: Number(m.trades) || 0 },
        { key: 'notes', icon: 'fa-regular fa-note-sticky', n: Number(m.notes) || 0 },
      ]
    },
    hasWork(item) {
      const m = item.missing || {}
      return isRestorable(item.matchStatus) || item.matchStatus === 'CHANGED' || !!item.fieldDiffs
        || ['accounts', 'monetary', 'trades', 'notes'].some(k => Number(m[k]) > 0)
    },
    rowClass(item) {
      if (!item) return ''
      // status bar: orange = something to recover/review, green = 100% the same
      return ['bk-row', `bk-row--${(item.matchStatus || '').toLowerCase()}`,
        this.hasWork(item) ? 'bk-row--todo' : 'bk-row--done bk-row--same']
    },
  },
}
</script>
